/**
 * Error Reporter - Captures JavaScript errors and sends to OverSkill for AI debugging
 *
 * Captures:
 * - Unhandled errors (window.onerror)
 * - Unhandled promise rejections
 * - Manual error reports
 *
 * Errors are sent to the parent window via postMessage when in iframe context.
 * Only enabled when app is viewed in OverSkill editor iframe.
 */

import type { ErrorData, PlatformConfig, ErrorReporterInstance } from './types';

export class ErrorReporter implements ErrorReporterInstance {
  private enabled = false;
  private isInIframe = false;
  private reportedErrors = new Set<string>(); // Dedupe errors
  private maxReportedErrors = 50; // Prevent memory leaks
  // In-flight dev-server lookup for a module-load failure (#9259).
  private compileErrorLookup: Promise<ErrorData | null> | null = null;

  constructor(private config: PlatformConfig) {
    // Detect iframe status early
    this.isInIframe = typeof window !== 'undefined' && window.parent !== window && window.parent !== null;

    if (this.shouldEnable()) {
      this.enabled = true;
      this.init();
    }
  }

  /**
   * Determine if error reporting should be enabled
   */
  private shouldEnable(): boolean {
    if (typeof window === 'undefined') return false;

    // Only enable if in iframe (being viewed in OverSkill editor)
    if (!this.isInIframe) {
      return false;
    }

    // Enable in development or when in iframe
    const appConfig = window.APP_CONFIG || {};
    return appConfig.ENVIRONMENT === 'development' || this.isInIframe;
  }

  /**
   * Initialize error handlers
   */
  private init(): void {
    // Capture unhandled errors (script-execution errors)
    window.addEventListener('error', (event) => {
      this.handleError({
        type: 'error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      this.handleError({
        type: 'unhandledrejection',
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    });

    // BUILD ERROR FIX (Apr 2026): Capture Vite/build-time errors that
    // window.onerror MISSES. Two channels:
    //
    //   1. Resource load failures (HTTP 500/404 on `<script type="module">`,
    //      `<link rel="stylesheet">`, etc.). These do NOT bubble to window.onerror —
    //      you must listen in CAPTURE phase to see them.
    //      Trigger: Vite returns 500 on /src/main.tsx because of a TS/JSX error.
    //
    //   2. Vite's `<vite-error-overlay>` custom element. When Vite hits a compile
    //      error mid-HMR, it injects this element into the DOM with the formatted
    //      error message. window.onerror NEVER fires for these.
    //
    // Both routes report as `type: 'build-error'` and the postMessage uses
    // `overskill-build-error` (vs `app_error` for runtime errors) so the
    // editor's error_detector_controller routes them straight to AI auto-fix.
    this.setupResourceErrorCapture();
    this.setupViteErrorOverlayDetection();

    // Chrome keeps only 250 resource entries by default and drops later ones;
    // a big app's broken module loads late, so the #9259 lookup needs room.
    try {
      performance.setResourceTimingBufferSize(1000);
    } catch {
      // unsupported: the lookup just sees fewer entries
    }

    this.debugLog('Error reporter initialized via postMessage (iframe mode)');
  }

  /**
   * Capture resource load failures (script/link/css/img) in CAPTURE phase.
   * These do NOT bubble to the regular `window.onerror`.
   */
  private setupResourceErrorCapture(): void {
    window.addEventListener(
      'error',
      (event) => {
        // Only handle resource errors (event.target is an element, not Window)
        const rawTarget = event.target as unknown;
        if (!rawTarget || rawTarget === window) return;
        const target = rawTarget as HTMLElement;
        if (!target.tagName) return;

        const tagName = target.tagName.toLowerCase();
        const isScript = tagName === 'script';
        const isStyle = tagName === 'link' || tagName === 'style';
        if (!isScript && !isStyle) return;

        // Extract URL of the failed resource
        const src =
          (target as HTMLScriptElement).src ||
          (target as HTMLLinkElement).href ||
          '';

        // Filter: only act on Vite-served modules (e.g. /src/main.tsx?import=...)
        // or any same-origin module fetch. Skip CDN failures (those are infra issues,
        // not code bugs) so we don't auto-fix transient network blips.
        if (!isAppModuleUrl(src)) return;

        // BUG FIX (v1.0.9, May 2026): Vite serves inline <script> blocks from
        // index.html via the html-proxy mechanism — URLs like
        // `/index.html?html-proxy&index=0.js`. The previous regex matched the
        // trailing `.js` and let these through, then `split('?')[0]` collapsed
        // the displayed filename to `/index.html`, producing a synthetic
        // "Failed to load module: /index.html (HTTP 5xx/404)" report whenever
        // the sprite waked from hibernation and these probes transiently 5xxd.
        // The editor then fired the AI auto-fix loop on a phantom error.
        // Skip html-proxy URLs explicitly (they're Vite-internal, not user code).
        if (src.includes('html-proxy')) return;

        // Extract a meaningful filename (strip ?import, ?t=timestamp, etc.)
        const filename = src.replace(window.location.origin, '').split('?')[0];

        // Defense-in-depth: any URL whose base path resolves to a `.html` file
        // is not a user app module. Vite never serves real source files with
        // a `.html` extension, so this should never be a legitimate build
        // error — skip rather than emit a confusing auto-fix request.
        if (filename.endsWith('.html')) return;

        // NO FABRICATED STATUS (v1.0.12, Aug 2026 — support ticket OS-RT8B28).
        // A resource `error` event on <script>/<link> carries no response
        // detail, so `event.status` is ALWAYS undefined here and the previous
        // `|| '5xx/404'` fallback printed an invented status on every single
        // report. It reads as a measurement: in OS-RT8B28 it was taken as one
        // and the customer was told a flaky preview was "on our end", i.e. a
        // server fault, which the string was never evidence of.
        //
        // Report the absence instead. The numeric branch is kept — not dead
        // code by intent but by the current event shape — so that any channel
        // that does supply a real status (a future fetch-based loader, a
        // synthesized report) still renders it, and 0 is treated as absent
        // because it is the "no response" sentinel, never an HTTP status.
        const rawStatus = (event as Event & { status?: number }).status;
        const statusLabel =
          typeof rawStatus === 'number' && rawStatus > 0
            ? `HTTP ${rawStatus}`
            : 'status unknown';

        this.reportModuleLoadFailure({
          type: 'build-error',
          message: `Failed to load module: ${filename} (${statusLabel})`,
          filename,
        });
      },
      true // capture phase — required for resource errors
    );
  }

  /**
   * RECOVER THE REAL COMPILE ERROR (v1.0.16, Oct 2026 — issue #9259, OS-YZAB3E).
   *
   * When ONE component fails to compile, the browser fires a single resource
   * error on the ENTRY script (`/src/main.tsx`), because a module graph fails
   * as a whole. The event names the wrong file and carries no detail. Vite
   * does push the real error to its overlay, but only to the client it
   * considers current: with a second preview frame or tab open, this page
   * gets nothing else, and the editor (its popup, the Fix prompt, the
   * builder's preview-console tool) saw only "(status unknown)". That cost a
   * customer four charged Fix turns that never saw the error.
   *
   * The dev server still holds the error: re-requesting the broken module
   * returns HTTP 500 with Vite's error page, whose inline script declares
   * `const error = {...}` (message, file, plugin, code frame). So: re-fetch
   * the same-origin app modules this page loaded that did not come back OK,
   * stop at the first 500 whose page parses, and report THAT as an ordinary
   * build error. It is also written to console.error so the preview console
   * capture (the builder's diagnostics tool) carries it.
   *
   * If nothing turns up, the original "Failed to load module … (status
   * unknown)" report goes out unchanged: the #3494 recovery path keys on
   * exactly that wording. When there is nothing to re-fetch it goes out
   * synchronously, as before.
   */
  private reportModuleLoadFailure(fallback: ErrorData): void {
    const candidates = failedModuleCandidates();
    if (candidates.length === 0 || typeof fetch !== 'function') {
      this.handleError(fallback);
      return;
    }

    // One lookup per burst: an entry failure often arrives with sibling
    // failures (a CSS link, a second entry), and they share one answer.
    if (!this.compileErrorLookup) {
      this.compileErrorLookup = findDevServerCompileError(candidates)
        .catch(() => null)
        .finally(() => {
          this.compileErrorLookup = null;
        });
    }

    this.compileErrorLookup.then((compileError) => {
      if (!compileError) {
        this.handleError(fallback);
        return;
      }
      if (this.reportedErrors.has(errorKey(compileError))) return;
      try {
        const where = compileError.line
          ? `${compileError.file}:${compileError.line}${compileError.column ? `:${compileError.column}` : ''}`
          : compileError.file;
        const plugin = compileError.plugin ? `[plugin:${compileError.plugin}] ` : '';
        console.error(
          `${plugin}${where}: ${compileError.message}` +
            (compileError.stack ? `\n${compileError.stack}` : '')
        );
      } catch {
        // never throw from the reporter
      }
      this.handleError(compileError);
    });
  }

  /**
   * Watch for Vite's compile-error overlay. Vite injects a `<vite-error-overlay>`
   * shadow-DOM custom element when a TS/JSX/PostCSS error occurs mid-HMR.
   * Scrape the error text and bridge it to the editor.
   */
  private setupViteErrorOverlayDetection(): void {
    if (typeof MutationObserver === 'undefined' || !document.body) {
      // body might not be ready yet; defer
      if (typeof window !== 'undefined') {
        window.addEventListener('DOMContentLoaded', () =>
          this.setupViteErrorOverlayDetection()
        );
      }
      return;
    }

    const seen = new WeakSet<Element>();

    const handleOverlay = (overlay: Element) => {
      if (seen.has(overlay)) return;
      seen.add(overlay);

      // Vite's overlay structure (as of v5+):
      //   <vite-error-overlay>
      //     #shadow-root
      //       .message-body, .file, .frame, .stack, .plugin
      const shadow =
        (overlay as Element & { shadowRoot?: ShadowRoot }).shadowRoot ||
        overlay;

      const messageEl = shadow.querySelector('.message-body, .message, pre');
      const fileEl = shadow.querySelector('.file');
      const frameEl = shadow.querySelector('.frame');
      const pluginEl = shadow.querySelector('.plugin');

      const message =
        (messageEl?.textContent || overlay.textContent || 'Vite compile error').trim();
      const filename = fileEl?.textContent?.trim() || undefined;
      const stack = frameEl?.textContent?.trim() || undefined;
      const plugin = pluginEl?.textContent?.trim() || undefined;

      this.handleError({
        type: 'build-error',
        message,
        filename,
        stack,
        plugin,
      });
    };

    // Check for an already-present overlay (race on initial load)
    document.querySelectorAll('vite-error-overlay').forEach(handleOverlay);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return; // ELEMENT_NODE
          const el = node as Element;
          if (el.tagName?.toLowerCase() === 'vite-error-overlay') {
            handleOverlay(el);
          }
          // Also check descendants
          el.querySelectorAll?.('vite-error-overlay').forEach(handleOverlay);
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Handle an error event
   */
  private handleError(error: ErrorData): void {
    // Create unique key for deduplication
    const key = errorKey(error);

    // Skip if we've already reported this exact error recently
    if (this.reportedErrors.has(key)) {
      return;
    }

    // Add to dedupe set
    this.reportedErrors.add(key);

    // Prevent memory leak - clear old errors if too many
    if (this.reportedErrors.size > this.maxReportedErrors) {
      const entries = Array.from(this.reportedErrors);
      this.reportedErrors = new Set(entries.slice(-this.maxReportedErrors / 2));
    }

    // Send error report
    this.sendErrorReport(error);
  }

  /**
   * Send error report via postMessage to parent window (iframe-only)
   *
   * NOTE: HTTP fallback was removed (Feb 2026) because:
   * 1. shouldEnable() already prevents initialization outside iframe
   * 2. Cross-origin HTTP to overskill.com from *.overskill.app always fails CORS
   * 3. Error reporting is only useful when AI is debugging in editor iframe
   */
  private sendErrorReport(error: ErrorData): void {
    // Only send via postMessage when in iframe (cross-origin safe)
    if (!this.isInIframe || !window.parent) {
      return;
    }

    try {
      const enrichedError: ErrorData = {
        ...error,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // BUILD ERROR FIX (Apr 2026): Build errors (Vite compile fails, module load
      // 500s) get the `overskill-build-error` envelope so the editor's
      // error_detector_controller routes them to AI auto-fix. Runtime exceptions
      // continue to use `app_error` (handled by console_log_capture_controller →
      // preview_error CustomEvent → error_popup_controller).
      const isBuildError =
        error.type === 'build-error' || error.type === 'resource-error';

      const envelope = isBuildError
        ? {
            type: 'overskill-build-error',
            error: {
              ...enrichedError,
              appId: this.config.appId,
            },
          }
        : {
            type: 'app_error',
            error: {
              ...enrichedError,
              appId: this.config.appId,
            },
          };

      window.parent.postMessage(envelope, '*');
    } catch {
      // Silent fail - never throw from error reporter
    }
  }

  /**
   * Debug log helper
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log(`[OverSkill Platform SDK] ${message}`, data || '');
    }
  }

  // Public API

  /**
   * Manually report an error
   */
  report(error: Error | ErrorData): void {
    if (!this.enabled) return;

    if (error instanceof Error) {
      this.handleError({
        type: 'error',
        message: error.message,
        stack: error.stack,
      });
    } else {
      this.handleError(error);
    }
  }

  /**
   * Check if reporter is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }
}

/** Dedupe key for one reported error (shared by the report and its console echo). */
function errorKey(error: ErrorData): string {
  return `${error.type}:${error.message}:${error.filename || ''}:${error.lineno || ''}`;
}

/**
 * True for a URL that looks like the app's own module (e.g. /src/main.tsx?t=…)
 * rather than a CDN asset. CDN failures are infra, not code bugs.
 */
function isAppModuleUrl(src: string): boolean {
  return (
    src.includes('/src/') ||
    src.includes('/app/') ||
    /\.(tsx|ts|jsx|js|css|scss)(\?|$)/.test(src)
  );
}

/** Upper bound on modules re-fetched for one failure (a big app loads hundreds). */
const MAX_COMPILE_ERROR_PROBES = 50;
/**
 * Browsers without `responseStatus` (Safari) cannot tell a failed module from
 * a healthy one, so every entry is a candidate; probe fewer of them.
 */
const MAX_UNKNOWN_STATUS_PROBES = 15;
/** Per-request ceiling, so a hung dev server cannot hold the report forever. */
const COMPILE_ERROR_PROBE_TIMEOUT_MS = 5000;
/** Ceiling on the whole lookup; past it the fallback report goes out. */
const COMPILE_ERROR_LOOKUP_BUDGET_MS = 8000;
/** The app's own API calls are resources too; never re-request those. */
const NON_MODULE_INITIATORS = new Set(['fetch', 'xmlhttprequest', 'beacon']);

/**
 * Same-origin app modules this page requested that did not come back OK:
 * status 0, unknown (a browser without `responseStatus`), or 400+. Failed
 * statuses go first, then newest first — the broken module is usually the
 * last thing the graph asked for.
 */
function failedModuleCandidates(): string[] {
  let entries: PerformanceResourceTiming[] = [];
  try {
    entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  } catch {
    return [];
  }

  const failed: string[] = [];
  const unknown: string[] = [];
  const seen = new Set<string>();
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    let url: URL;
    try {
      url = new URL(entry.name, window.location.href);
    } catch {
      continue;
    }
    if (url.origin !== window.location.origin) continue;
    if (NON_MODULE_INITIATORS.has(entry.initiatorType)) continue;
    if (!isAppModuleUrl(url.href) || url.href.includes('html-proxy')) continue;
    if (url.pathname.endsWith('.html')) continue;
    if (seen.has(url.href)) continue;
    seen.add(url.href);

    const status = (entry as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
    if (typeof status === 'number' && status >= 400) {
      failed.push(url.href);
    } else if (typeof status !== 'number' || status === 0) {
      unknown.push(url.href);
    }
  }
  return failed.concat(unknown.slice(0, MAX_UNKNOWN_STATUS_PROBES)).slice(0, MAX_COMPILE_ERROR_PROBES);
}

/** Re-request each candidate; the first one Vite answers with its error page wins. */
async function findDevServerCompileError(candidates: string[]): Promise<ErrorData | null> {
  const deadline = Date.now() + COMPILE_ERROR_LOOKUP_BUDGET_MS;
  for (const href of candidates) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return null;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), Math.min(COMPILE_ERROR_PROBE_TIMEOUT_MS, remaining)) : null;
    try {
      const response = await fetch(href, {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller?.signal,
      });
      if (response.status !== 500) continue;
      const parsed = parseViteErrorPage(await response.text(), href);
      if (parsed) return parsed;
    } catch {
      // network failure on one probe: try the next
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  return null;
}

/**
 * Parse Vite's dev-server error page. Its inline module script declares
 * `const error = <JSON>` on one line (Vite's errorMiddleware: message, stack,
 * id, frame, plugin, loc). Exported for tests.
 */
export function parseViteErrorPage(body: string, requestUrl: string): ErrorData | null {
  const match = body.match(/const error = (\{.*\})\s*$/m);
  if (!match) return null;

  let payload: {
    message?: unknown;
    frame?: unknown;
    plugin?: unknown;
    id?: unknown;
    loc?: { file?: unknown; line?: unknown; column?: unknown };
  };
  try {
    payload = JSON.parse(match[1]);
  } catch {
    return null;
  }
  if (!payload || typeof payload.message !== 'string' || !payload.message.trim()) return null;

  // The editor names files from the app root ("/src/…"). Vite's `loc.file` /
  // `id` are absolute paths inside the sandbox and name the file that actually
  // failed, which is not always the one requested (a stylesheet's broken
  // @import). Prefer that file, made app-relative; else the request's path.
  let requested: string;
  try {
    requested = new URL(requestUrl, window.location.href).pathname;
  } catch {
    requested = requestUrl.split('?')[0];
  }
  const diagnosticPath = [payload.loc?.file, payload.id].find(
    (p): p is string => typeof p === 'string' && p.length > 0
  );
  let file = requested;
  if (diagnosticPath) {
    const bare = diagnosticPath.split('?')[0];
    const srcAt = bare.indexOf('/src/');
    if (bare.endsWith(requested)) file = requested;
    else if (srcAt !== -1) file = bare.slice(srcAt);
  }

  // The diagnostic is the message up to its code frame. Babel
  // (vite:react-babel) appends the frame to the message and leaves `frame` /
  // `loc` empty; esbuild puts a "Transform failed" header first and the real
  // error on the next line, so keep every line before the frame. Some
  // plugins prefix the absolute sandbox path; drop it.
  const lines = payload.message.split('\n');
  let frameStart = lines.findIndex((l) => /^\s*>?\s*\d+\s*[|│]/.test(l) || l.includes('╭─['));
  if (frameStart === -1) frameStart = lines.length;
  let message = lines.slice(0, frameStart).join('\n').trim();
  const absolutePaths = [payload.id, payload.loc?.file].filter(
    (p): p is string => typeof p === 'string' && p.length > 0
  );
  for (const p of absolutePaths) {
    const bare = p.split('?')[0];
    if (message.startsWith(`${bare}: `)) {
      message = message.slice(bare.length + 2);
      break;
    }
  }
  if (!message) message = payload.message.trim();

  const embeddedFrame = lines.slice(frameStart).join('\n').trimEnd();
  const frame =
    (typeof payload.frame === 'string' && payload.frame.trim() ? payload.frame.trimEnd() : '') ||
    embeddedFrame ||
    undefined;
  // `loc` when the plugin gave one; else Babel's `>  7 |` frame marker, or
  // the `─[ src/x.tsx:7:13 ]` header Vite 8's oxc transform draws.
  const oxcHeader = payload.message.match(/─\[\s*\S+?:(\d+):(\d+)\s*\]/);
  const frameLine = frame?.match(/^>\s*(\d+)\s*\|/m);
  const line =
    typeof payload.loc?.line === 'number'
      ? payload.loc.line
      : frameLine
        ? Number(frameLine[1])
        : oxcHeader
          ? Number(oxcHeader[1])
          : undefined;
  const column =
    typeof payload.loc?.column === 'number' ? payload.loc.column : oxcHeader ? Number(oxcHeader[2]) : undefined;

  return {
    type: 'build-error',
    message,
    filename: file,
    file,
    lineno: line,
    colno: column,
    line,
    column,
    stack: frame,
    plugin: typeof payload.plugin === 'string' && payload.plugin ? payload.plugin : undefined,
  };
}
